import { onMounted, onUnmounted, watch, computed, ref } from 'vue'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import { storeToRefs } from 'pinia'
import type { Handler } from 'mitt'
import { init } from 'commandbar'
import type { EventType } from 'commandbar/build/commandbar/src/shared/services/analytics/EventHandler'
import type { ProductConfig } from 'commandbar/build/internal/src/client/CommandBarClientSDK'
import { useFlag } from '@unleash/proxy-client-vue'

import { useUser } from 'src/store/modules/App/user'
import useCommandBarStore from '@/store/modules/App/commandBar'

import config from '@/config'
import { MOBILE_WIDTH } from '@constants/screenBreakpoints'
import type { SbTheme } from '@/components/ContentManager/AccountSettings/types'
import { useEmitter } from '@/utils/setup-application/emitter'
import { registerCustomComponents } from './customComponents/components'
import { RecordClient } from './records'
import { commandBarEvent } from './constants'
import {
  type RecordClientOperation,
  type SbRecordType,
  type RecordStrategyArgs as RecordClientArgs,
} from './types'
import { addBlockPreviewComponent } from './components/blockPreview'

export function useCommandBar(
  route: RouteLocationNormalizedLoaded,
  router: Router,
): void {
  const spaceId = computed(() => route?.params?.spaceId?.toString())
  const storyId = computed(() => route?.params?.storyId?.toString())
  const commandBarEventSubscriber = ref()
  const shouldFetchSpaceData = ref(true)
  const commandBarOrgId = process.env.VUE_APP_COMMAND_BAR_ORG_ID
  const isSpotlightFeatureEnabled = useFlag('spotlight')
  const isMobileWidth = ref(false)
  const { isBooted } = storeToRefs(useCommandBarStore())
  const shouldLoadCommandBar = computed(
    () => !isMobileWidth.value && !config.data.isBringYourOwnCloud(),
  )
  const userStore = useUser()
  const { getUserTheme, getUserId, isUserLoggedIn } = storeToRefs(userStore)

  function handleResize(): void {
    isMobileWidth.value = window.innerWidth < MOBILE_WIDTH
  }

  async function handleSpacesRecord(): Promise<void> {
    const spacesRecordClient = new RecordClient('spaces')
    await spacesRecordClient.execute('set', {})
  }

  async function handleSpaceRecords(
    operation: RecordClientOperation,
  ): Promise<void> {
    const spaceRecordClient = new RecordClient('space')
    await spaceRecordClient.execute(operation, getRecordClientArgs())
  }

  async function handleStoryRecords(
    operation: RecordClientOperation,
  ): Promise<void> {
    const recordClient = new RecordClient('story')
    await recordClient.execute(operation, getRecordClientArgs())
  }

  function getRecordClientArgs(): RecordClientArgs {
    return {
      spaceId: spaceId.value,
      storyId: storyId.value,
      params: route?.params,
      query: route?.query,
    } satisfies RecordClientArgs
  }

  const addEventSubscriber = (): void => {
    commandBarEventSubscriber.value = window?.CommandBar?.addEventSubscriber(
      (eventName: EventType) => handleCommandBarEvents(eventName),
    )
  }

  function handleCommandBarEvents(eventName: EventType): void {
    switch (eventName) {
      case commandBarEvent.open: {
        document.body.style.overflow = 'hidden'

        const scrollBarGap = 11
        document.body.style.paddingRight = `${scrollBarGap}px`
        break
      }
      case commandBarEvent.close:
      case commandBarEvent.commandExecution:
        setTimeout(() => {
          /*
            FYI, there is no `afterClose` event in the CommandBar SDK.
            Therefore, we used a timeout to wait for the modal to close
            before removing the padding-right from the body.
            Otherwise, as the 'close' event is triggered as soon as you close the modal,
            a layout shift occurs when the user closes the modal
            as the padding is removed before the modal is actually closed.
           */
          document.body.style.overflow = ''
          document.body.style.paddingRight = '0'
        }, 300)
        break
      default:
        break
    }
  }

  async function removeRecords(): Promise<void> {
    await handleSpaceRecords('remove')
  }

  function recordEventHandler(recordType: SbRecordType): void {
    const recordClient = new RecordClient(recordType)
    recordClient.execute('set', getRecordClientArgs())
  }

  const emitter = useEmitter()
  function addEmitterListeners(): void {
    emitter?.on(
      'set-commandbar-record',
      recordEventHandler as unknown as Handler<typeof recordEventHandler>,
    )
  }
  function removeEmitterListeners(): void {
    emitter?.off(
      'set-commandbar-record',
      recordEventHandler as unknown as Handler<typeof recordEventHandler>,
    )
  }

  function setBootStatus(): void {
    useCommandBarStore()?.setBootStatus(
      window?.CommandBar?.shareState()?.isBooted,
    )
  }

  async function handleCommandBarBoot(): Promise<void> {
    let products: ProductConfig = ['nudges', 'checklists']
    if (isSpotlightFeatureEnabled.value) {
      products = [...products, 'spotlight']
    }

    const routerFunc = (url: string) => router.push(url)
    window?.CommandBar?.addRouter(routerFunc)

    setUiTheme(getUserTheme.value)

    await window?.CommandBar?.boot(
      getUserId.value?.toString(),
      {},
      { products },
    )

    window?.CommandBar?.addCallback(
      'navAndTriggerNudge',
      async (_: unknown, context: { spaceId: string }) => {
        await router.push({
          name: 'ListStoriesRoute',
          params: {
            spaceId: context.spaceId,
            pipelineId: '0',
            releaseId: '0',
            folderId: '0',
          },
        })

        // 8801 is the tour id for "DemoOn - Story home"
        // we need to open it as soon as the user navigates to the story list
        window?.CommandBar?.triggerNudge(8801)
      },
    )

    setBootStatus()

    if (isSpotlightFeatureEnabled.value) {
      await handleSpacesRecord()
      await handleSpaceRecords('set')
      await handleStoryRecords('set')
      addBlockPreviewComponent()
    }

    addEventSubscriber()
  }

  function handleInitCommandBar(): void {
    if (!commandBarOrgId) {
      console.warn('No org specified for CommandBar.init')
      return
    }

    if (!isBooted.value) {
      init(commandBarOrgId)
      registerCustomComponents()
      addEmitterListeners()
    }
  }

  function setUiTheme(theme: SbTheme | undefined): void {
    const prefersDark =
      (window?.matchMedia('(prefers-color-scheme: dark)').matches &&
        theme === 'sync-system') ||
      theme?.includes('dark')

    const themeMode = prefersDark ? 'dark_mode' : 'light_mode'

    window?.CommandBar?.setThemeMode(themeMode)
  }

  watch(spaceId, async (newSpaceId, oldSpaceId) => {
    const isDifferentSpace = newSpaceId !== oldSpaceId
    const isInSpace = !!newSpaceId

    if (shouldFetchSpaceData.value) {
      await handleSpaceRecords('set')
      addBlockPreviewComponent()
      shouldFetchSpaceData.value = false
      return
    }

    if (!isInSpace) {
      await removeRecords()
      shouldFetchSpaceData.value = true
      return
    }

    if (isDifferentSpace) {
      await removeRecords()
      await handleSpaceRecords('set')
      addBlockPreviewComponent()
      return
    }
  })

  watch(isMobileWidth, (isMobile) => {
    if (isMobile && window.CommandBar) {
      window.CommandBar.shutdown()
      setBootStatus()
    }
  })

  watch(storyId, async (newStoryId) => {
    let operation: RecordClientOperation = 'set'
    if (!newStoryId || !spaceId.value) {
      operation = 'remove'
    }
    await handleStoryRecords(operation)
  })

  watch(
    () => route?.name,
    async (routeName) => {
      switch (routeName) {
        case 'Logout':
          window?.CommandBar?.shutdown()
          setBootStatus()
          break
        case 'Login':
        case 'Signup':
        case 'OnboardingRoute':
        case 'ResetPassword':
        case 'ResendConfirmation':
        case 'ConfirmAccount':
        case 'SSOAuth':
        case 'SSOLoginPage':
        case 'AcceptInvitation':
        case 'ForgotPassword':
          break
        default: {
          const { loadUserInfo, checkUserLoggedIn } = userStore
          await checkUserLoggedIn()

          if (isUserLoggedIn.value) {
            await loadUserInfo()
            if (shouldLoadCommandBar.value && !isBooted.value) {
              handleInitCommandBar()
              await handleCommandBarBoot()
            }
          }

          break
        }
      }
    },
    { immediate: true },
  )

  watch(
    () => getUserTheme.value,
    (theme) => {
      setUiTheme(theme)
    },
  )

  onMounted((): void => {
    window.addEventListener('resize', handleResize)
  })

  onUnmounted((): void => {
    commandBarEventSubscriber.value.then((unSubscribe: () => void) =>
      unSubscribe(),
    )
    window?.CommandBar?.shutdown()
    removeEmitterListeners()
    window.removeEventListener('resize', handleResize)
  })
}
